"use client"

import dynamic from "next/dynamic"
import { Button } from "@ui/shad"
import { cn } from "@ui/utils"
import type { FileUIPart } from "ai"
import { FileText, Paperclip, X } from "lucide-react"

import { PromptInputHeader, usePromptInputAttachments } from "@/components/ai-elements/prompt-input"

const PdfAttachmentPreview = dynamic(
  () =>
    import("@/components/chat-threads/pdf-attachment-preview").then(({ PdfAttachmentPreview }) => PdfAttachmentPreview),
  { ssr: false }
)

export interface PendingStorageAttachment {
  id: string
  bookId: string
  name: string
  provider?: string
}

export interface ChatAttachment {
  key: string
  url?: string
  file?: Blob
  filename?: string
  mediaType?: string
  size?: number
}

export function ChatPendingAttachments({
  storageAttachments = [],
  onRemoveStorageAttachment,
  compact = false,
}: {
  storageAttachments?: PendingStorageAttachment[]
  onRemoveStorageAttachment?: (id: string, bookId: string) => void
  compact?: boolean
}) {
  const attachments = usePromptInputAttachments()
  if (attachments.files.length === 0 && storageAttachments.length === 0) return null

  return (
    <PromptInputHeader className={cn("overflow-visible pb-0", compact ? "px-3 pt-3" : "px-4 pt-4")}>
      <div className="flex w-full flex-wrap items-start gap-2">
        {attachments.files.map((file) => (
          <PendingAttachmentTile
            key={file.id}
            file={file}
            compact={compact}
            onRemove={() => attachments.remove(file.id)}
          />
        ))}
        {storageAttachments.map((attachment) => (
          <PendingStorageAttachmentTile
            key={`${attachment.bookId}:${attachment.id}`}
            attachment={attachment}
            compact={compact}
            onRemove={() => onRemoveStorageAttachment?.(attachment.id, attachment.bookId)}
          />
        ))}
      </div>
    </PromptInputHeader>
  )
}

export function ChatAttachmentTiles({
  attachments,
  compact = false,
  className,
}: {
  attachments: ChatAttachment[]
  compact?: boolean
  className?: string
}) {
  if (attachments.length === 0) return null

  return (
    <div className={cn("ml-auto flex max-w-full flex-wrap items-start justify-end gap-2", className)}>
      {attachments.map((attachment) => (
        <ChatAttachmentTile key={attachment.key} attachment={attachment} compact={compact} />
      ))}
    </div>
  )
}

function PendingStorageAttachmentTile({
  attachment,
  onRemove,
  compact,
}: {
  attachment: PendingStorageAttachment
  onRemove: () => void
  compact: boolean
}) {
  return (
    <div
      className={cn(
        "group bg-background relative flex h-10 max-w-full items-center gap-2 rounded-md border px-2 shadow-sm",
        compact ? "w-52" : "w-64"
      )}
    >
      <div className="bg-blue-background flex size-6 shrink-0 items-center justify-center rounded">
        <Paperclip className="text-blue-accent size-4" />
      </div>
      <div className="min-w-0 flex-1 truncate text-sm font-medium">{attachment.name}</div>
      {attachment.provider && <div className="text-muted-foreground shrink-0 text-xs">{attachment.provider}</div>}
      <PendingAttachmentRemoveButton onRemove={onRemove} />
    </div>
  )
}

function PendingAttachmentTile({
  file,
  onRemove,
  compact,
}: {
  file: FileUIPart & { id: string; file?: File }
  onRemove: () => void
  compact: boolean
}) {
  const isImage = file.mediaType?.startsWith("image/")
  const isPdf = isPdfAttachment(file.mediaType, file.filename)
  const extension = attachmentExtension(file)

  if (isImage && file.url) {
    return (
      <div
        className={cn(
          "group relative w-fit rounded-xl",
          compact ? "max-w-[min(20rem,100%)]" : "max-w-[min(32rem,100%)]"
        )}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          alt={file.filename ?? "Attachment"}
          className={cn(
            "bg-background max-w-full rounded-xl border object-contain shadow-sm",
            compact ? "h-16" : "h-24"
          )}
          src={file.url}
        />
        <PendingAttachmentRemoveButton onRemove={onRemove} />
      </div>
    )
  }

  if (isPdf && file.url) {
    const height = compact ? 64 : 96
    const maxWidth = compact ? 320 : 512
    return (
      <div className={cn("group relative w-fit rounded-xl", compact ? "max-w-[20rem]" : "max-w-[32rem]")}>
        <PdfAttachmentPreview
          url={file.url}
          file={file.file}
          filename={file.filename}
          className={cn(compact ? "h-16 max-w-[20rem]" : "h-24 max-w-[32rem]")}
          width={compact ? 128 : 192}
          height={height}
          maxWidth={maxWidth}
          fallback={<PendingPdfFallback file={file} compact={compact} />}
        />
        <PendingAttachmentRemoveButton onRemove={onRemove} />
      </div>
    )
  }

  return (
    <div
      className={cn(
        "group bg-background relative flex h-10 max-w-full items-center gap-2 rounded-md border px-2 shadow-sm",
        compact ? "w-52" : "w-64"
      )}
    >
      <div className="bg-red-background flex size-6 shrink-0 items-center justify-center rounded">
        <FileText className="text-red-accent size-4" />
      </div>
      <div className="min-w-0 flex-1 truncate text-sm font-medium">{file.filename ?? "Attachment"}</div>
      {extension && <div className="text-muted-foreground shrink-0 text-xs uppercase">{extension}</div>}
      <PendingAttachmentRemoveButton onRemove={onRemove} />
    </div>
  )
}

function PendingPdfFallback({ file, compact }: { file: FileUIPart; compact: boolean }) {
  return (
    <div
      className={cn(
        "bg-background flex max-w-full flex-col items-center justify-center gap-1 rounded-xl border shadow-sm",
        compact ? "h-16 w-32" : "h-24 w-48"
      )}
    >
      <div
        className={cn("bg-red-background flex items-center justify-center rounded-md", compact ? "size-7" : "size-8")}
      >
        <FileText className={cn("text-red-accent", compact ? "size-4" : "size-5")} />
      </div>
      <div className="max-w-20 truncate px-1 text-center text-xs font-medium">{file.filename ?? "PDF"}</div>
    </div>
  )
}

function PendingAttachmentRemoveButton({ onRemove }: { onRemove: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="iconXs"
      aria-label="Remove attachment"
      onClick={onRemove}
      className="bg-foreground text-background hover:bg-foreground/90 hover:text-background absolute -top-1.5 -right-1.5 size-[18px] rounded-full shadow-sm"
    >
      <X className="size-3" />
    </Button>
  )
}

function ChatAttachmentTile({ attachment, compact }: { attachment: ChatAttachment; compact: boolean }) {
  const isImage = attachment.mediaType?.startsWith("image/")
  const isPdf = isPdfAttachment(attachment.mediaType, attachment.filename)

  if (isImage && attachment.url) {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        className={cn("block", compact ? "max-w-[min(20rem,100%)]" : "max-w-[min(32rem,100%)]")}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          alt={attachment.filename ?? "Attachment"}
          src={attachment.url}
          className={cn(
            "bg-background max-w-full rounded-xl border object-contain shadow-sm",
            compact ? "h-16" : "h-24"
          )}
        />
      </a>
    )
  }

  if (isPdf && (attachment.url || attachment.file)) {
    const preview = (
      <PdfAttachmentPreview
        url={attachment.url ?? attachment.key}
        file={attachment.file}
        filename={attachment.filename}
        className={cn(compact ? "h-16 max-w-[20rem]" : "h-24 max-w-[32rem]")}
        width={compact ? 128 : 192}
        height={compact ? 64 : 96}
        maxWidth={compact ? 320 : 512}
        fallback={<ChatAttachmentFileCard attachment={attachment} compact={compact} />}
      />
    )

    if (!attachment.url) return preview

    return (
      <a href={attachment.url} target="_blank" rel="noreferrer" className="block">
        {preview}
      </a>
    )
  }

  if (attachment.url) {
    return (
      <a href={attachment.url} target="_blank" rel="noreferrer" className="block">
        <ChatAttachmentFileCard attachment={attachment} compact={compact} />
      </a>
    )
  }

  return <ChatAttachmentFileCard attachment={attachment} compact={compact} />
}

function ChatAttachmentFileCard({ attachment, compact }: { attachment: ChatAttachment; compact: boolean }) {
  const subtitleParts = [attachment.mediaType, formatFileSize(attachment.size)].filter(Boolean) as string[]
  const subtitle = subtitleParts.join(" • ")

  return (
    <div
      className={cn(
        "bg-background flex max-w-full items-center rounded-xl border shadow-sm",
        compact ? "w-64 gap-2 px-2.5 py-2" : "w-80 gap-3 px-3 py-2.5"
      )}
    >
      <div
        className={cn("text-muted-foreground flex shrink-0 items-center justify-center", compact ? "size-8" : "size-9")}
      >
        <FileText className={compact ? "size-5" : "size-6"} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{attachment.filename ?? "Attachment"}</div>
        {subtitle && <div className="text-muted-foreground truncate text-xs">{subtitle}</div>}
      </div>
    </div>
  )
}

function attachmentExtension(file: Pick<FileUIPart, "filename" | "mediaType">) {
  const filename = file.filename ?? ""
  const extension = filename.includes(".") ? filename.split(".").pop() : undefined
  if (extension) return extension

  if (file.mediaType?.includes("/")) return file.mediaType.split("/").pop()

  return undefined
}

function isPdfAttachment(mediaType?: string, filename?: string) {
  return mediaType === "application/pdf" || filename?.toLowerCase().endsWith(".pdf") === true
}

function formatFileSize(bytes?: number): string | undefined {
  if (!bytes || bytes <= 0) return undefined
  const units = ["B", "KB", "MB", "GB"]
  let value = bytes
  let unitIndex = 0
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex++
  }
  const formatted = value >= 10 || unitIndex === 0 ? Math.round(value).toString() : value.toFixed(1)
  return `${formatted} ${units[unitIndex]}`
}
